import React, { useState } from 'react';
import notify from '../../../shared/feedback/notifications';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import ZentuvoInvoicingApi from '../ZentuvoInvoicingApi';
import { useInvoiceDatesQuery } from '../queries/useInvoiceDatesQuery';
import { useContentProvidersQuery } from '../queries/useContentProvidersQuery';

const DEFAULT_CONTENT_PROVIDER_VALUE = 7;
const DEFAULT_CHECKED_INVOICE_DATE_COUNT = 2;

function toggleValue<T>(values: T[], value: T): T[] {
    return values.indexOf(value) >= 0 ? values.filter((item) => item !== value) : values.concat(value);
}

const InvoiceReportComponentPage: React.FC = () => {
    const invoiceDatesQuery = useInvoiceDatesQuery();
    const contentProvidersQuery = useContentProvidersQuery();

    const [selectedInvoiceDatesOverride, setSelectedInvoiceDates] = useState<string[] | null>(null);
    const [selectedContentProvidersOverride, setSelectedContentProviders] = useState<number[] | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);

    const invoiceDates = invoiceDatesQuery.data || [];
    const contentProviders = contentProvidersQuery.data || [];

    const defaultSelectedInvoiceDates = invoiceDates.slice(0, DEFAULT_CHECKED_INVOICE_DATE_COUNT);
    const defaultSelectedContentProviders = contentProviders.some(
        (provider) => provider.value === DEFAULT_CONTENT_PROVIDER_VALUE
    )
        ? [DEFAULT_CONTENT_PROVIDER_VALUE]
        : [];

    const selectedInvoiceDates = selectedInvoiceDatesOverride ?? defaultSelectedInvoiceDates;
    const selectedContentProviders = selectedContentProvidersOverride ?? defaultSelectedContentProviders;

    const canDownload =
        selectedInvoiceDates.length > 0 && selectedContentProviders.length > 0 && !isDownloading;

    const handleDownload = async () => {
        if (!canDownload) {
            return;
        }

        setIsDownloading(true);
        try {
            await ZentuvoInvoicingApi.downloadInvoiceReportCsv(selectedInvoiceDates, selectedContentProviders);
            notify.success('Invoice report generated');
        } catch (error) {
            notify.failure('Failed to generate invoice report');
            console.error('invoice report download error', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const isLoading = invoiceDatesQuery.isLoading || contentProvidersQuery.isLoading;
    const hasError = invoiceDatesQuery.isError || contentProvidersQuery.isError;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
                <Typography variant="h5">Invoice report</Typography>
                <Typography variant="body2" color="text.secondary">
                    Select one or more invoice dates and content providers, then download the report as a CSV
                    file (opens in Microsoft Excel).
                </Typography>
            </Box>

            {hasError && (
                <Alert severity="error">Failed to load report options. Please reload the page.</Alert>
            )}

            {isLoading && <Alert severity="info">Loading report options...</Alert>}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
                <Paper sx={{ padding: 3, minWidth: 260 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Typography variant="subtitle1">Invoice dates</Typography>
                        {invoiceDates.length > 0 && (
                            <Box>
                                <Link
                                    component="button"
                                    type="button"
                                    variant="body2"
                                    onClick={() => setSelectedInvoiceDates(invoiceDates.slice())}
                                    sx={{ marginRight: 2 }}
                                >
                                    Select all
                                </Link>
                                <Link
                                    component="button"
                                    type="button"
                                    variant="body2"
                                    onClick={() => setSelectedInvoiceDates([])}
                                >
                                    Clear
                                </Link>
                            </Box>
                        )}
                    </Stack>
                    <FormGroup sx={{ marginTop: 1 }}>
                        {invoiceDates.map((invoiceDate) => (
                            <FormControlLabel
                                key={invoiceDate}
                                control={
                                    <Checkbox
                                        checked={selectedInvoiceDates.indexOf(invoiceDate) >= 0}
                                        onChange={() =>
                                            setSelectedInvoiceDates((current) =>
                                                toggleValue(current ?? defaultSelectedInvoiceDates, invoiceDate)
                                            )
                                        }
                                    />
                                }
                                label={invoiceDate}
                            />
                        ))}
                    </FormGroup>
                    {!isLoading && invoiceDates.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            No invoice dates found for the last year.
                        </Typography>
                    )}
                </Paper>

                <Paper sx={{ padding: 3, minWidth: 260 }}>
                    <Typography variant="subtitle1">Content providers</Typography>
                    <FormGroup sx={{ marginTop: 1 }}>
                        {contentProviders.map((provider) => (
                            <FormControlLabel
                                key={provider.value}
                                control={
                                    <Checkbox
                                        checked={selectedContentProviders.indexOf(provider.value) >= 0}
                                        onChange={() =>
                                            setSelectedContentProviders((current) =>
                                                toggleValue(current ?? defaultSelectedContentProviders, provider.value)
                                            )
                                        }
                                    />
                                }
                                label={provider.label}
                            />
                        ))}
                    </FormGroup>
                </Paper>
            </Stack>

            <Box>
                <Button
                    variant="contained"
                    startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                    disabled={!canDownload}
                    onClick={handleDownload}
                >
                    {isDownloading ? 'Generating...' : 'Download CSV'}
                </Button>
                {(selectedInvoiceDates.length === 0 || selectedContentProviders.length === 0) && !isLoading && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginTop: 1 }}>
                        Select at least one invoice date and one content provider to download the report.
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default InvoiceReportComponentPage;
